
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import type { Observable } from 'rxjs';
import config from '../../../config';
import type {
  CreateCycleRequest,
  CreateCycleResponse,
  CycleWithForms,
  ListCycleAssignmentsResponse,
  UpdateCycleRequest,
  UpdateCycleResponse,
  ListCyclesByUserStatusResponse,
  PaginatedCyclesByUserStatusResponse,
  AssignedFormsResponse,
  CycleFormQuestionsResponse,
  CycleAnswersResponse,
  ListCyclesResponse
} from '../../shared/models/cycle-management.models';

@Injectable({
  providedIn: 'root'
})
export class CycleManagementService {
  /**
   * List cycles with optional filters and pagination
   */
  listCycles(params: {
    market_id?: number | number[];
    business_unit_name?: string;
    business_unit_names?: string[];
    status?: string | string[];
    search?: string;
    page_size?: number;
    page_number?: number;
  } = {}): Observable<ListCyclesResponse> {
    // Use POST with request body instead of GET with query params
    const body: Record<string, number | number[] | string | string[]> = {
      page_size: params.page_size || 10,
      page_number: params.page_number || 0
    };

    // Add market_id as array if provided
    if (params.market_id !== undefined && params.market_id !== null) {
      body['market_id'] = Array.isArray(params.market_id) ? params.market_id : [params.market_id];
    }

    // Add business_unit_names as array if provided
    if (params.business_unit_names && params.business_unit_names.length > 0) {
      body['business_unit_names'] = params.business_unit_names;
    } else if (params.business_unit_name) {
      body['business_unit_names'] = [params.business_unit_name];
    }

    // Add status as array if provided
    if (params.status) {
      body['status'] = Array.isArray(params.status) ? params.status : [params.status];
    }

    // Add search if provided
    if (params.search) {
      body['search'] = params.search;
    }

    return this.http.post<ListCyclesResponse>(`${this.apiUrl}/cycles/search`, body);
  }

  /**
   * Create a new cycle
   * Note: Request body is automatically compressed with gzip via GzipInterceptor
   */
  createCycle(body: CreateCycleRequest): Observable<CreateCycleResponse> {
    return this.http.post<CreateCycleResponse>(`${this.apiUrl}/cycles`, body);
  }

  /**
   * Save a draft cycle
   * Note: Request body is automatically compressed with gzip via GzipInterceptor
   */
  saveDraftCycle(body: CreateCycleRequest): Observable<CreateCycleResponse> {
    return this.http.post<CreateCycleResponse>(`${this.apiUrl}/cycles`, body);
  }

  /**
   * Get details of a specific cycle by ID
   */
  getCycleDetail(id: number): Observable<CycleWithForms> {
    return this.http.get<CycleWithForms>(`${this.apiUrl}/cycles/${id}`);
  }

  /**
   * Update an existing cycle
   * Note: Request body is automatically compressed with gzip via GzipInterceptor
   */
  updateCycle(id: number, body: UpdateCycleRequest): Observable<UpdateCycleResponse> {
    return this.http.put<UpdateCycleResponse>(`${this.apiUrl}/cycles/${id}`, body);
  }

  /**
   * Delete a cycle (typically for draft cycles only)
   */
  deleteCycle(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/cycles/${id}`);
  }

  /**
   * Get cycle assignments by cycle ID
   * @param cycleId The cycle ID
   * @param businessUnitIds Optional array of business unit IDs to filter by
   */
  getCycleAssignments(cycleId: number, businessUnitIds?: number[]): Observable<ListCycleAssignmentsResponse> {
    let params = new HttpParams();

    // Add business_unit_ids as comma-separated string if provided
    if (businessUnitIds && businessUnitIds.length > 0) {
      params = params.set('business_unit_ids', businessUnitIds.join(','));
    }

    return this.http.get<ListCycleAssignmentsResponse>(
      `${this.apiUrl}/cycles/assignments/${cycleId}`,
      { params }
    );
  }

  /**
   * Get cycles by user ID and status
   */
  getCyclesByUserAndStatus(
    userId: number,
    status: 'active' | 'draft' | 'closed',
    marketId?: number
  ): Observable<ListCyclesByUserStatusResponse> {
    let url = `${this.apiUrl}/cycles/users/${userId}/status/${status}`;

    if (marketId !== undefined && marketId !== null) {
      url += `?market_id=${marketId}`;
    }

    return this.http.get<ListCyclesByUserStatusResponse>(url);
  }

  /**
   * Get paginated cycles by user ID and status
   */
  getPaginatedCyclesByUserAndStatus(
    userId: number,
    status: 'active' | 'draft' | 'closed',
    pageSize = 10,
    pageNumber = 0,
    marketId?: number
  ): Observable<PaginatedCyclesByUserStatusResponse> {
    const params = new URLSearchParams();
    params.set('page_size', pageSize.toString());
    params.set('page_number', pageNumber.toString());

    if (marketId !== undefined && marketId !== null) {
      params.set('market_id', marketId.toString());
    }

    return this.http.get<PaginatedCyclesByUserStatusResponse>(
      `${this.apiUrl}/cycles/users/${userId}/status/${status}/paginated?${params.toString()}`
    );
  }

  /**
   * Get assigned cycle forms for a user
   */
  getAssignedForms(cycleId: number, userId: number): Observable<AssignedFormsResponse> {
    return this.http.get<AssignedFormsResponse>(
      `${this.apiUrl}/cycles/${cycleId}/users/${userId}/assigned-forms`
    );
  }

  /**
   * Get questions for a cycle form
   */
  getCycleFormQuestions(cycleId: number, cycleFormId: number): Observable<CycleFormQuestionsResponse> {
    return this.http.get<CycleFormQuestionsResponse>(
      `${this.apiUrl}/cycles/${cycleId}/forms/${cycleFormId}/questions`
    );
  }

  /**
   * Get cycle assignment answers with optional activity filter
   */
  getCycleAssignmentAnswers(
    cycleAssignmentId: number,
    activityNumber?: number
  ): Observable<CycleAnswersResponse> {
    const params = activityNumber !== undefined
      ? `?activity_number=${activityNumber}`
      : '';
    return this.http.get<CycleAnswersResponse>(
      `${this.apiUrl}/cycle-assignments/${cycleAssignmentId}/answers${params}`
    );
  }

  /**
   * Save or submit activity answers
   */
  saveActivityAnswers(
    cycleId: number,
    formId: number,
    payload: {
      user_prid: string;
      prid: string;
      display_name: string;
      worker_prid: string;
      activity_number: number;
      activity_details: {
        root_cause_id?: number;
        mitigation_id?: number;
        root_cause_mitigation_comment?: string;
        is_monitoring_complete?: boolean;
        is_form_not_applicable?: boolean;
        form_not_applicable_reason?: string;
        reference_number?: string;
      };
      activity_status: {
        id?: number; // For updating existing status
        activity_status: 'draft' | 'submitted' | 'approved' | 'rejected';
      };
      answers: {
        id?: number; // For updating existing answer
        cycle_question_id: number;
        answer: string;
        comments: string;
        attachment_id?: number;
      }[];
    }
  ): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.apiUrl}/cycles/${cycleId}/forms/${formId}/activities/save`,
      payload
    );
  }

  /**
   * Delete activity answers (only draft status allowed)
   */
  deleteActivityAnswers(
    cycleActivityId: number,
    userPrid: string
  ): Observable<{ success: boolean; message: string }> {
    return this.http.request<{ success: boolean; message: string }>(
      'DELETE',
      `${this.apiUrl}/activities/${cycleActivityId}`,
      {
        body: { user_prid: userPrid }
      }
    );
  }

  /**
   * Mark cycle assignment form as not applicable
   */
  markFormNotApplicable(
    cycleAssignmentId: number,
    payload: {
      is_form_not_applicable: boolean;
      form_not_applicable_reason: string;
      updated_by: string;
    }
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/cycle-assignments/${cycleAssignmentId}/mark-not-applicable`,
      payload
    );
  }

  /**
   * Approve an activity
   * @param cycleActivityId The ID of the activity to approve
   * @param approverPrid PRID of the user approving
   * @param approverComments Optional comments from approver
   */
  approveActivity(
    cycleActivityId: number,
    approverPrid: string,
    approverComments?: string
  ): Observable<{ success: boolean; message: string; status_id?: number }> {
    return this.http.post<{ success: boolean; message: string; status_id?: number }>(
      `${this.apiUrl}/activities/${cycleActivityId}/approve`,
      {
        approver_prid: approverPrid,
        approver_comments: approverComments
      }
    );
  }

  /**
   * Reject an activity
   * @param cycleActivityId The ID of the activity to reject
   * @param approverPrid PRID of the user rejecting
   * @param approverComments Comments from approver (required for rejection)
   * @param isActionRequired Whether action is required on rejection
   */
  rejectActivity(
    cycleActivityId: number,
    approverPrid: string,
    approverComments: string,
    isActionRequired: boolean
  ): Observable<{ success: boolean; message: string; status_id?: number }> {
    return this.http.post<{ success: boolean; message: string; status_id?: number }>(
      `${this.apiUrl}/activities/${cycleActivityId}/reject`,
      {
        approver_prid: approverPrid,
        approver_comments: approverComments,
        is_action_required_on_reject: isActionRequired
      }
    );
  }

  /**
   * Mark a cycle assignment as completed
   * @param cycleAssignmentId The ID of the cycle assignment to mark as completed
   * @param updatedBy PRID of the user marking the assignment as completed
   * @returns Observable with success status and message
   */
  markAssignmentCompleted(
    cycleAssignmentId: number,
    updatedBy: string
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/cycle-assignments/${cycleAssignmentId}/mark-completed`,
      { updated_by: updatedBy }
    );
  }

  /**
   * Extend cycle deadline
   * @param cycleId The ID of the cycle
   * @param payload The request payload with new_deadline and updated_by
   * @returns Observable with success status and message
   */
  extendCycleDeadline(
    cycleId: number,
    payload: {
      new_deadline: string;
      updated_by: string;
    }
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/cycles/${cycleId}/extend-deadline`,
      payload
    );
  }

  /**
   * Update assignment deadline
   * @param assignmentId The ID of the cycle assignment
   * @param payload The request payload with custom_deadline and updated_by
   * @returns Observable with success status and message
   */
  updateAssignmentDeadline(
    assignmentId: number,
    payload: {
      custom_deadline: string;
      updated_by: string;
    }
  ): Observable<{ success: boolean; message: string }> {
    return this.http.put<{ success: boolean; message: string }>(
      `${this.apiUrl}/cycle-assignments/${assignmentId}/deadline`,
      payload
    );
  }
  /**
   * Update the deadline for all assignments of a specific user in a cycle
   * @param cycleId The cycle ID
   * @param userId The user ID
   * @param payload The request payload with custom_deadline and updated_by
   * @returns Observable with update result
   */
  updateAssignmentDeadlineByCycleUser(
    cycleId: number,
    userId: number,
    payload: {
      custom_deadline: string | null;
      updated_by: string;
    }
  ): Observable<{ updated_count: number; cycle_id: number; user_id: number; custom_deadline: string | null; message: string }> {
    return this.http.put<{ updated_count: number; cycle_id: number; user_id: number; custom_deadline: string | null; message: string }>(
      `${this.apiUrl}/cycles/${cycleId}/users/${userId}/deadline`,
      payload
    );
  }
  private apiUrl = `${config.API_GATEWAY_ENV_ENDPOINT}`;
  private http = inject(HttpClient);

  /**
   * Create a new cycle
   * @param cycleData The request body for creating a cycle
   */

  // Add more cycle management methods as needed (e.g., getCycles, updateCycle, deleteCycle)
}
